import { useMemo, useState } from "react";
import { AlertCircle, Users } from "lucide-react";
import { Skeleton } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import type { DashboardMode } from "@/lib/dashboard-mode";
import { OperacaoChips } from "./OperacaoChips";
import { ProfissionalDoDiaCard, type MembroDoDia } from "./ProfissionalDoDiaCard";
import { TeamMemberDrawer } from "./TeamMemberDrawer";
import { ColaboradorDoLabCard } from "./ColaboradorDoLabCard";
import { getRoleLabel } from "./role-meta";
import { ehDonoOuSocio } from "@bookdental/schemas";

/** No laboratório todo mundo pode assumir trabalho de bancada — não existe a divisão
 *  "atende / não atende" que a clínica tem, então o filtro por perfil não faz sentido lá. */
const FILTROS_CLINICA = [
  { chave: "todos", rotulo: "Todos" },
  { chave: "dentistas", rotulo: "Dentistas" },
  { chave: "apoio", rotulo: "Equipe de apoio" },
] as const;

/**
 * Quem é PROFISSIONAL, e não só quem tem determinado papel de acesso.
 *
 * O papel `DENTIST` resolve o caso óbvio. Já o proprietário pode ser tanto o dentista dono da
 * clínica quanto alguém puramente administrativo — por isso, pra ele, o que decide é o perfil:
 * CRO, especialidade cadastrada ou consultas de fato na agenda. Assim o dono que atende aparece
 * entre os dentistas, e o dono que só administra aparece como apoio, sem métricas que não são dele.
 */
function ehProfissional(m: { role: string; croNumber?: string | null; especialidades: string[] }, temConsultas: boolean) {
  if (m.role === "DENTIST") return true;
  if (!ehDonoOuSocio(m.role)) return false;
  return !!m.croNumber || m.especialidades.length > 0 || temConsultas;
}

/**
 * "Visão geral" da Equipe: como está a operação agora.
 *
 * Complementa — não substitui — a aba de acessos: ali se administra quem entra e o que pode; aqui
 * se acompanha quem está atendendo, quanto cada um carrega e o que vem a seguir. Tudo derivado das
 * consultas reais (`team.operacaoDoDia`), sem número decorativo.
 */
export function TeamVisaoGeral({ mode }: { mode: DashboardMode }) {
  const [filtro, setFiltro] = useState<string>("todos");
  const [membroAberto, setMembroAberto] = useState<string | null>(null);
  const { user } = useAuth();
  const membersQuery = trpc.team.list.useQuery();
  const operacaoQuery = trpc.team.operacaoDoDia.useQuery();

  const porMembro = operacaoQuery.data?.porMembro;
  const membros: MembroDoDia[] = useMemo(() => {
    return ((membersQuery.data ?? []) as any[]).map((m) => {
      // A que vale nesta organização — o servidor já resolveu qual das duas fontes usar. Ler
      // `m.user.specialties` aqui ignoraria o que a clínica definiu sobre a pessoa.
      const especialidades = (m.especialidade?.rotulos ?? []) as string[];
      const op = porMembro?.[m.user.id];
      return {
        membershipId: m.id,
        userId: m.user.id,
        // Mesmo fallback da tabela de membros: e-mail no lugar do nome faria o cartão parecer outra
        // pessoa a cada aba.
        nome: m.user.name || "(sem nome)",
        avatarUrl: m.user.image ?? null,
        funcao: m.customRole?.label ?? getRoleLabel(m.role, mode),
        especialidades,
        ehProfissional: ehProfissional(
          { role: m.role, croNumber: m.user.croNumber, especialidades },
          (op?.hoje ?? 0) > 0 || (op?.noMes ?? 0) > 0,
        ),
        suspenso: m.status === "SUSPENDED",
        responsabilidades: (m.assignedProcedureTypeIds ?? []).length,
      };
    });
  }, [membersQuery.data, mode, porMembro]);

  const visiveis = useMemo(() => {
    if (filtro === "dentistas") return membros.filter((m) => m.ehProfissional);
    if (filtro === "apoio") return membros.filter((m) => !m.ehProfissional);
    return membros;
  }, [membros, filtro]);

  const contagem = useMemo(
    () => ({
      todos: membros.length,
      dentistas: membros.filter((m) => m.ehProfissional).length,
      apoio: membros.filter((m) => !m.ehProfissional).length,
    }),
    [membros],
  );

  // Laboratório puro: sem agenda de consultas, a leitura é a produção. Organização híbrida segue
  // na leitura de clínica — ela tem consulta de verdade, e o lado laboratório vive em /lab/producao.
  const ehLab = !!operacaoQuery.data?.resumoLab && !operacaoQuery.data?.resumo;
  const FILTROS = ehLab ? ([{ chave: "todos", rotulo: "Todos" }] as const) : FILTROS_CLINICA;
  const carregando = membersQuery.isLoading || operacaoQuery.isLoading;
  const erro = membersQuery.error ?? operacaoQuery.error;

  if (erro) {
    return (
      <div className="px-6 py-10 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
        <p className="mt-3 text-sm font-medium text-zinc-900 dark:text-foreground">
          Não foi possível carregar a operação da equipe
        </p>
        <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">{erro.message}</p>
        <button
          type="button"
          onClick={() => {
            membersQuery.refetch();
            operacaoQuery.refetch();
          }}
          className="mt-4 inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    // Mesma margem lateral das abas e da aba de acessos (`px-6`, sem cap próprio). Cheguei a
    // limitar em 1400px pra o cartão não esticar em monitor largo, mas isso recuava o conteúdo ~100px
    // de cada lado e ele passava a flutuar no meio do painel, desalinhado das abas logo acima e da
    // tabela da outra aba. Alinhar as duas abas vale mais que economizar largura no cartão.
    <div className="space-y-4 px-6 py-4">
      <OperacaoChips
        resumo={operacaoQuery.data?.resumo}
        resumoLab={operacaoQuery.data?.resumoLab}
        carregando={carregando}
      />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Equipe hoje</h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            {ehLab
              ? "Acompanhe as próteses sob responsabilidade de cada colaborador."
              : "Acompanhe a agenda e a situação operacional dos profissionais."}
          </p>
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrar profissionais">
          {FILTROS.map((f) => (
            <button
              key={f.chave}
              type="button"
              onClick={() => setFiltro(f.chave)}
              aria-pressed={filtro === f.chave}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                filtro === f.chave
                  ? "bg-brand-600 font-semibold text-white"
                  : "text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted",
              )}
            >
              {f.rotulo}
              <span
                className={cn(
                  "tabular-nums",
                  filtro === f.chave ? "text-white/80" : "text-zinc-400 dark:text-muted-foreground/70",
                )}
              >
                {contagem[f.chave]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {carregando ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[188px] w-full rounded-xl" />
          ))}
        </div>
      ) : visiveis.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50 p-8 text-center dark:border-border dark:bg-muted/20">
          <Users className="mx-auto h-10 w-10 text-zinc-300 dark:text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-foreground">
            {membros.length === 0 ? "Nenhum membro na equipe" : "Ninguém nesse filtro"}
          </p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            {membros.length === 0
              ? "Convide colegas na aba Membros e acessos."
              : "Troque o filtro para ver os demais."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-2">
          {visiveis.map((m) =>
            ehLab ? (
              <ColaboradorDoLabCard
                key={m.membershipId}
                membro={m}
                operacao={operacaoQuery.data?.porMembroLab?.[m.userId]}
                onAbrir={() => setMembroAberto(m.membershipId)}
              />
            ) : (
              <ProfissionalDoDiaCard
                key={m.membershipId}
                membro={m}
                operacao={operacaoQuery.data?.porMembro?.[m.userId]}
                expediente={operacaoQuery.data?.expediente ?? null}
                // Só oferece "Ver agenda" quando a agenda daquela pessoa é mesmo visível pra quem
                // está olhando — botão que leva a uma tela vazia é pior que botão nenhum.
                podeVerAgenda={
                  !!operacaoQuery.data?.podeVerDetalheDaAgenda || m.userId === user?.id
                }
                onAbrir={() => setMembroAberto(m.membershipId)}
              />
            ),
          )}
        </div>
      )}

      <TeamMemberDrawer membershipId={membroAberto} onClose={() => setMembroAberto(null)} />
    </div>
  );
}
